import { API } from '@/api'
import { Volume } from '@/api/type-server'
import { InstancePlanItem, InstancePlanPrice, ItemPaymentType, ItemPriceType } from '@/consts/order'
import { Image } from '@/models/image'
import { Machine } from '@/models/machine'
import { Snapshot } from '@/models/snapshot'
import { VOLUME_TYPE } from '@/models/volume'
import type { CreateInstanceBoundNATInfo } from '@/pages/create-instance/types'
import { buildBaseResourceDesc } from '@/pages/create-instance/utils/extraStorage'

import { resolveMappedPortProtocol } from './portProtocol'

export {
  findSubscriptionPlan,
  formatSubscriptionDuration,
  getPlanPriceByType,
  getSubscriptionCreditDecision,
  getSubscriptionPaymentType,
  getSubscriptionPriceCent,
  getSubscriptionQuantity,
  getValidPriceCent,
  hasSufficientRechargeCredits,
  type SubscriptionCreditDecision,
  type SubscriptionMachine,
  type SubscriptionPlan,
  supportsSubscription,
} from './subscription'

export function buildCreateInstancePayload({
  machine,
  count,
  checkedImage,
  checkedSnap,
  mountVolume,
  mountPath,
  readWriteMode,
  boundNAT,
  remark,
  teamMountVolume,
  teamMountPath,
  teamReadWriteMode,
  extraResourceDesc,
}: {
  machine: Machine
  count: number
  checkedImage?: Image
  checkedSnap?: Snapshot
  mountVolume?: Volume
  mountPath: string
  readWriteMode: boolean
  boundNAT?: CreateInstanceBoundNATInfo
  remark?: string
  teamMountVolume?: Volume
  teamMountPath?: string
  teamReadWriteMode?: boolean
  extraResourceDesc?: string
}) {
  const destinationPorts = boundNAT?.destinationPorts ?? []
  const presetPorts = checkedSnap?.presetPorts ?? checkedImage?.ports ?? []

  return {
    auto_password: true,
    hardware_qty: count,
    image_id: checkedImage?.id,
    from_snapshot_id: checkedSnap?.id,
    machine_category: machine.machineCategory,
    mount_infos: JSON.stringify(
      mountVolume
        ? [
            {
              source: '/',
              dest: mountPath,
              readOnly: readWriteMode,
              domain: mountVolume?.domain ?? 0,
            },
          ]
        : [],
    ),
    team_mount_infos: JSON.stringify(
      teamMountVolume
        ? [
            {
              source: '/',
              dest: teamMountPath ?? '/team_data',
              readOnly: teamReadWriteMode ?? true,
              domain: teamMountVolume?.domain ?? 0,
            },
          ]
        : [],
    ),
    vnc_switcher: true,
    resource_desc: buildBaseResourceDesc(machine, count),
    vnc_support: checkedImage?.supportVnc ?? false,
    remark,
    nat_res_id: boundNAT?.resID,
    extra_resource_desc: extraResourceDesc,
    ports:
      boundNAT && destinationPorts.length > 0
        ? JSON.stringify(
            destinationPorts.map((port) => ({
              srcPort: Number(port),
              protocol: resolveMappedPortProtocol(Number(port), presetPorts),
              destPort: Number(port),
            })),
          )
        : undefined,
  }
}

export function filterValidVolumes(volumes: Volume[], domains: number[] = []): Volume[] {
  const filteredVolumes = volumes.filter((volume) => {
    if (volume.type === VOLUME_TYPE.SNAPSHOT) {
      return false
    }

    if (!domains.includes(volume.domain)) {
      return false
    }

    if (volume.quota_gb === 0 && volume.usage_mb === 0) {
      return false
    }

    return true
  })

  return filteredVolumes
}

export async function fetchInstancePlans(): Promise<InstancePlanItem[]> {
  const res: any = await API.getItems({
    page: 1,
    per_page: 1000,
    catalog: 7,
    domain: 0,
  })

  // const ordersRes: any = await API.getOrders({ catalog: 7, status_filter: OrderStatus.ALL })

  if (res.code !== 0) {
    console.error('GET /items error', res)
    return []
  }

  return (res.items ?? []).map(
    (item: any): InstancePlanItem => ({
      itemId: item.id,
      resID: item.resID,
      catalog: item.catalog,
      paymentType: item.paymentType as ItemPaymentType,
      prices: (item.prices ?? []).map(
        (p: any): InstancePlanPrice => ({
          priceCent: p.priceCent,
          type: p.type as ItemPriceType,
        }),
      ),
      domain: item.domain,
    }),
  )
}
